import { EConfigurationNames } from '@ConfigLoader/ConfigFiles';
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import SpineWrapper from '@Core/Animation/SpineWrapper';
import { GameComponent } from '@Core/GameComponent/GameComponent';
import ReelSymbol from '@Core/Reels/ReelSymbol';
import { ESymbolAnimations } from '@Core/Reels/SymbolAnimations';
import { TrackEntry } from '@esotericsoftware/spine-pixi-v8';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { TSpecialSymbolIdsByGameType } from '@GFTypes/GameStoreTypes';
import { ESpecialGamesSymbolDrawAnimationType } from '@GFTypes/SpecialGames';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { EntryCondition } from '@Managers/StateMachine/EntryCondition';
import { LeaveCondition } from '@Managers/StateMachine/LeaveCondition';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { StateManager } from '@Managers/StateManager/StateManager';
import randomValue from '@Tools/RandomValue';
import { GameScene } from '..';
import { SPECIAL_GAME_CONFIGS_GAMESTORE_KEYS, SPECIAL_GAME_PREFIX_PER_TYPE } from '../../Types/GameStoreTypes/SpecialGames';
import { getLastNotEndedSpecialGames } from './SpecialGamesHelper';

export class Intro extends GameComponent {
    public static readonly TOTAL_SPINS_SLOT_NAME = 'T_FREEGAMES_TEXT_2';
    private intro?: SpineWrapper;
    private readonly info?: SpineWrapper;
    private readonly specialSymbol?: SpineWrapper;
    private readonly triggerSymbolsAnimationsLeaveCondition = new LeaveCondition<EGameStates>('waitForTriggerSymbolsAnimations');
    private readonly specialGamesStartInfoLeaveCondition = new LeaveCondition<EGameStates>('waitForStartInfoAnimations');
    private readonly specialGamesIntroLeaveCondition = new LeaveCondition<EGameStates>('waitForIntroAnimation');
    private readonly specialGamesSpecialSymbolLeaveCondition = new LeaveCondition<EGameStates>('waitForSpecialSymbolDrawAnimation');
    private readonly specialGamesIntroEndLeaveCondition = new LeaveCondition<EGameStates>('waitForIntroEndAnimation');
    private readonly retriggerLeaveCondition = new LeaveCondition<EGameStates>('waitForRetriggerSymbolsAnimations');
    private randomIds: number[] = [];
    constructor(
        public readonly scene: GameScene,
        intro?: SpineWrapper,
        info?: SpineWrapper,
        specialSymbol?: SpineWrapper,
        private readonly specialSymbolsMap?: TSpecialSymbolIdsByGameType
    ) {
        super(scene);
        this.createSubStates();

        const hasInfoAnimations = info?.eventManager.hasGameEventAnimations([
            EGameEvents.SPECIAL_GAMES_START_INFO_SHOW,
            EGameEvents.SPECIAL_GAMES_START_INFO_HIDE
        ]);

        const hasIntroAnimations = intro?.eventManager.hasGameEventAnimations([EGameEvents.SPECIAL_GAMES_INTRO, EGameEvents.SPECIAL_GAMES_INTRO_END]);

        const hasSpecialSymbolAnimations = specialSymbol?.eventManager.hasGameEventAnimations([
            EGameEvents.SPECIAL_GAMES_SYMBOL_DRAW,
            EGameEvents.SPECIAL_GAMES_SYMBOL_SELECT
        ]);

        if (hasInfoAnimations) this.info = info;
        if (hasIntroAnimations) this.intro = intro;
        if (hasSpecialSymbolAnimations) this.specialSymbol = specialSymbol;

        EventsCenter.on(EGameStates.PRE_SPECIAL_GAMES_INTRO_STATE, this.onPreSpecialGamesIntroState, this);
        EventsCenter.on(EGameStates.PRE_SPECIAL_GAMES_RETRIGGER, this.onPreSpecialGamesIntroState, this);
        EventsCenter.on(EGameStates.SPECIAL_GAMES_TRIGGER, this.onTriggerSymbolsAnimationsState, this);
        EventsCenter.on(EGameStates.SPECIAL_GAMES_RETRIGGER, this.onRetriggerSymbolsAnimationsState, this);
        EventsCenter.on(EGameStates.PRE_WIN_SPECIAL_GAMES_RETRIGGER, this.onRetriggerSymbolsAnimationsState, this);
        EventsCenter.on(EGameStates.SPECIAL_GAMES_START_INFO_STATE, this.onSpecialGamesStartInfoState, this);
        EventsCenter.on(EGameStates.SPECIAL_GAMES_SPECIAL_SYMBOL_STATE, this.onSpecialGamesSpecialSymbolState, this);
        EventsCenter.on(EGameStates.SPECIAL_GAMES_INTRO_END_STATE, this.onSpecialGamesIntroEndState, this);
        EventsCenter.on(EGameStates.SPECIAL_GAMES_INTRO_STATE, this.onSpecialGamesIntroState, this);
        EventsCenter.on(EGameEvents.SPECIAL_GAMES_RESTORE, this.onSpecialGamesRestore, this);
    }

    onCreate(): void {
        if (!this.intro) {
            const reelsSpine = this.scene.reelsBackground.getReelsSpine();
            const hasIntroAnimations = reelsSpine?.eventManager.hasGameEventAnimations(
                [EGameEvents.SPECIAL_GAMES_INTRO, EGameEvents.SPECIAL_GAMES_INTRO_END],
                true
            );
            if (hasIntroAnimations) this.intro = reelsSpine;
        }
    }

    private createSubStates(): void {
        const isFirstSpecialGamesSpin = new EntryCondition<EGameStates>('checkIfIsStartOfSpecialGames', () => {
            //TODO: Check for nested special games
            const specialGames =
                this.gameStore.get(EGameStoreKeys.SpinOutcome)?.specialGames || this.gameStore.get(EGameStoreKeys.ResumedSpinOutcome)?.specialGames;
            const isFirstSpecialGamesSpin = specialGames?.currentSpin === 0;
            if (isFirstSpecialGamesSpin) return true;
            return false;
        });

        const retriggerEntryContition = new EntryCondition<EGameStates>('checkIfIsRetrigger', () => {
            const retriggerPositions = this.gameStore.get(EGameStoreKeys.SpinOutcome)?.specialGames?.retriggerPositions;
            return !!retriggerPositions && retriggerPositions.length > 0;
        });

        const isFirstSpecialGamesSpinOrRetrigger = new EntryCondition<EGameStates>('checkSsFirstSpecialGamesSpinOrRetrigger', () => {
            return isFirstSpecialGamesSpin.check() || retriggerEntryContition.check();
        });

        const isInMultiStepWin = new EntryCondition<EGameStates>('checkIfIsInMultiStepWin', () => {
            const stepsLength = this.gameStore.get(EGameStoreKeys.SpinOutcome)?.steps.length;
            const currentStepIndex = this.gameStore.get(EGameStoreKeys.BaseWinPresentationState).currentWinPresentationStepIndex;
            return stepsLength > 1 && currentStepIndex === stepsLength - 1;
        });

        const hasInfoSpine = new EntryCondition<EGameStates>('checkIfIsStartOfSpecialGames', () => {
            return !!this.info;
        });
        const hasIntroSpine = new EntryCondition<EGameStates>('checkIfIsStartOfSpecialGames', () => {
            return !!this.intro;
        });
        const hasSpecialSymbolSpine = new EntryCondition<EGameStates>('checkIfIsStartOfSpecialGames', () => {
            return !!this.specialSymbol;
        });

        const subStates = {
            [EGameStates.PRE_SPECIAL_GAMES_INTRO_STATE]: {
                entryConditions: [isFirstSpecialGamesSpinOrRetrigger],
                leaveConditions: []
            },
            [EGameStates.SPECIAL_GAMES_TRIGGER]: {
                entryConditions: [isFirstSpecialGamesSpin],
                leaveConditions: [this.triggerSymbolsAnimationsLeaveCondition]
            },
            [EGameStates.SPECIAL_GAMES_RETRIGGER]: {
                entryConditions: [retriggerEntryContition],
                leaveConditions: [this.retriggerLeaveCondition]
            },
            [EGameStates.SPECIAL_GAMES_START_INFO_STATE]: {
                entryConditions: [isFirstSpecialGamesSpin, hasInfoSpine],
                leaveConditions: [this.specialGamesStartInfoLeaveCondition]
            },
            [EGameStates.SPECIAL_GAMES_INTRO_STATE]: {
                entryConditions: [isFirstSpecialGamesSpin, hasIntroSpine],
                leaveConditions: [this.specialGamesIntroLeaveCondition]
            },
            [EGameStates.SPECIAL_GAMES_SPECIAL_SYMBOL_STATE]: {
                entryConditions: [isFirstSpecialGamesSpin, hasSpecialSymbolSpine],
                leaveConditions: [this.specialGamesSpecialSymbolLeaveCondition]
            },
            [EGameStates.SPECIAL_GAMES_INTRO_END_STATE]: {
                entryConditions: [isFirstSpecialGamesSpin, hasIntroSpine],
                leaveConditions: [this.specialGamesIntroEndLeaveCondition]
            },
            [EGameStates.POST_SPECIAL_GAMES_INTRO_STATE]: {
                entryConditions: [isFirstSpecialGamesSpin],
                leaveConditions: []
            }
        };

        const preWinFeaturesSubStates = {
            [EGameStates.PRE_SPECIAL_GAMES_RETRIGGER]: {
                entryConditions: [isInMultiStepWin, retriggerEntryContition],
                leaveConditions: []
            },
            [EGameStates.PRE_WIN_SPECIAL_GAMES_RETRIGGER]: {
                entryConditions: [isInMultiStepWin, retriggerEntryContition],
                leaveConditions: [this.retriggerLeaveCondition]
            }
        };

        StateManager.assignSubStates(EGameStates.PRE_WIN_FEATURES_STATE, preWinFeaturesSubStates);
        StateManager.assignSubStates(EGameStates.POST_WIN_FEATURES_STATE, subStates);
    }

    private onPreSpecialGamesIntroState(): void {
        const currentSpecialGameType = this.gameStore.get(EGameStoreKeys.CurrentSpecialGamesType);
        if (!currentSpecialGameType) throw new Error('Cannot transition to special games');
        const spinOutcome = this.scene.game.gameStore.get(EGameStoreKeys.SpinOutcome)?.gameTypeAfter
            ? this.scene.game.gameStore.get(EGameStoreKeys.SpinOutcome)
            : this.scene.game.gameStore.get(EGameStoreKeys.ResumedSpinOutcome);

        if (!spinOutcome) throw new Error('Cannot transition to special games');
        const { gameTypeAfter } = spinOutcome;
        const specialSymbolId = this.specialSymbolsMap?.get(gameTypeAfter);

        if (specialSymbolId) {
            const skinName = `id_${specialSymbolId}`;
            const specialSymbolSkeleton = this.specialSymbol?.spine.skeleton;
            const reelsSkeleton = this.scene.reelsBackground.getReelsSpine().spine.skeleton;
            const specialGamesConfig = ConfigLoader.getConfiguration(EConfigurationNames.GAME).specialGames;
            const symbolDrawAnimationType = specialGamesConfig.symbolDrawAnimationType ?? ESpecialGamesSymbolDrawAnimationType.CLASSIC;

            if (specialSymbolSkeleton?.data.findSkin(skinName)) {
                specialSymbolSkeleton.setSkin(skinName);
                specialSymbolSkeleton.setupPoseSlots();
            } else if (specialSymbolSkeleton && symbolDrawAnimationType === ESpecialGamesSymbolDrawAnimationType.REEL) {
                const { numberOfPlaceholderSlots, winningSlotIndex, randomIdsToSpin, attachementPrefix, slotPrefix } = specialGamesConfig.reelAnimationOptions!;
                this.randomIds = [];
                for (let i = 0; i < numberOfPlaceholderSlots; i++) {
                    const id = i === winningSlotIndex ? specialSymbolId : randomIdsToSpin[randomValue(0, randomIdsToSpin.length - 1)];
                    this.randomIds.push(id);
                    specialSymbolSkeleton.setAttachment(`${slotPrefix}${i + 1}`, `${attachementPrefix}${id < 10 ? '0' : ''}${id}_bl`);
                }
            }

            if (reelsSkeleton?.data.findSkin(skinName)) {
                reelsSkeleton.setSkin(skinName);
                reelsSkeleton.setupPoseSlots();
            }
        }
        const specialGamesConfigGameStoreKey = SPECIAL_GAME_CONFIGS_GAMESTORE_KEYS.get(currentSpecialGameType)!;
        const config = this.scene.game.gameStore.get(specialGamesConfigGameStoreKey);
        if (!config) return;
        const { totalSpins } = config;
        this.scene.game.gameStore.set(
            EGameStoreKeys.TextVariables,
            {
                [`${SPECIAL_GAME_PREFIX_PER_TYPE.get(currentSpecialGameType)}SpinsTotal`]: String(totalSpins)
            },
            true
        );
    }

    private onSpecialGamesRestore(): void {
        const currentSpecialGames = getLastNotEndedSpecialGames(this.gameStore);
        if (!currentSpecialGames || currentSpecialGames.currentSpin === 0) return;

        this.onPreSpecialGamesIntroState();
    }

    private async onTriggerSymbolsAnimationsState(): Promise<void> {
        const currentSpecialGameType = this.gameStore.get(EGameStoreKeys.CurrentSpecialGamesType)!;
        const currentSpecialGames = this.gameStore.get(SPECIAL_GAME_CONFIGS_GAMESTORE_KEYS.get(currentSpecialGameType)!);
        if (!currentSpecialGameType) throw new Error('Cannot transition to special games');
        const visibleSymbols = this.gameStore.get(EGameStoreKeys.VisibleSymbols);
        const symbolsAnimationsCompleted: Promise<TrackEntry | void>[] = [];
        const triggerMask: boolean[][] = visibleSymbols.map((reel) => reel.map(() => false));
        currentSpecialGames.triggerPositions.forEach(([reel, row]) => {
            if (triggerMask[reel]) triggerMask[reel][row] = true;
        });
        const playAndWait = (spine: SpineWrapper, anim: ESymbolAnimations, symbol: ReelSymbol) => {
            const finalAnimation = symbol.isMultiSymbol ? symbol.getMultiSymbolAnimationVariant(anim) : anim;
            spine.play(finalAnimation);
            symbolsAnimationsCompleted.push(spine.waitForAnimationEvent(finalAnimation, 'complete').promise);
        };

        for (let reel = 0; reel < visibleSymbols.length; reel++) {
            for (let row = 0; row < visibleSymbols[reel].length; row++) {
                const spine = visibleSymbols[reel][row].symbolAnimation;

                if (triggerMask[reel][row]) {
                    if (spine.eventManager.hasGameEventAnimation(ESymbolAnimations.SYMBOL_SPECIAL_GAMES_WIN)) {
                        playAndWait(spine, ESymbolAnimations.SYMBOL_SPECIAL_GAMES_WIN, visibleSymbols[reel][row]);
                    } else {
                        playAndWait(spine, ESymbolAnimations.WIN, visibleSymbols[reel][row]);
                    }
                } else {
                    spine.play(ESymbolAnimations.NOTWIN);
                }
            }
        }
        await Promise.all(symbolsAnimationsCompleted);
        this.triggerSymbolsAnimationsLeaveCondition.resolve();
    }

    private async onRetriggerSymbolsAnimationsState(): Promise<void> {
        const isInMultiStepWin = () => {
            const stepsLength = this.gameStore.get(EGameStoreKeys.SpinOutcome)?.steps.length;
            const currentStepIndex = this.gameStore.get(EGameStoreKeys.BaseWinPresentationState).currentWinPresentationStepIndex;
            return stepsLength > 1 && currentStepIndex === stepsLength - 1;
        };

        const isInPreWin = this.gameStore.get(EGameStoreKeys.CurrentGameState) === EGameStates.PRE_WIN_FEATURES_STATE;
        const symbolsAnimationsCompleted: Promise<TrackEntry>[] = [];

        if (!isInMultiStepWin() || (isInMultiStepWin() && isInPreWin)) {
            const currentSpecialGameType = this.gameStore.get(EGameStoreKeys.CurrentSpecialGamesType)!;
            const currentSpecialGames = this.gameStore.get(SPECIAL_GAME_CONFIGS_GAMESTORE_KEYS.get(currentSpecialGameType)!);
            const visibleSymbols = this.gameStore.get(EGameStoreKeys.VisibleSymbols);
            const triggerMask: boolean[][] = visibleSymbols.map((reel) => reel.map(() => false));
            currentSpecialGames.retriggerPositions.forEach(([reel, row]) => (triggerMask[reel][row] = true));
            const playAndWait = (spine: any, anim: ESymbolAnimations) => {
                spine.play(anim);
                symbolsAnimationsCompleted.push(spine.waitForAnimationEvent(anim, 'complete').promise);
            };

            for (let reel = 0; reel < visibleSymbols.length; reel++) {
                for (let row = 0; row < visibleSymbols[reel].length; row++) {
                    const spine = visibleSymbols[reel][row].symbolAnimation;

                    if (triggerMask[reel][row]) {
                        if (spine.eventManager.hasGameEventAnimation(ESymbolAnimations.SYMBOL_SPECIAL_GAMES_WIN)) {
                            playAndWait(spine, ESymbolAnimations.SYMBOL_SPECIAL_GAMES_WIN);
                        } else {
                            playAndWait(spine, ESymbolAnimations.WIN);
                        }
                    } else {
                        spine.play(ESymbolAnimations.NOTWIN);
                    }
                }
            }
        }

        await Promise.all(symbolsAnimationsCompleted);

        if (isInPreWin) return this.retriggerLeaveCondition.resolve();

        EventsCenter.emit(EGameEvents.SPECIAL_GAMES_RETRIGGER_SHOW);
        await this.info?.waitForAnimationEvent(EGameEvents.SPECIAL_GAMES_RETRIGGER_SHOW, 'complete').promise;

        const infoPromise = new Promise<void>((resolve) => {
            this.scene.time.delayedCall(ConfigLoader.getConfiguration(EConfigurationNames.GAME).specialGames.retriggerInfoDuration, resolve);
        });

        await Promise.race([infoPromise, EventsCenter.waitForEvent(EGameEvents.CONTINUE)]);
        EventsCenter.emit(EGameEvents.SPECIAL_GAMES_RETRIGGER_HIDE);
        await this.info?.waitForAnimationEvent(EGameEvents.SPECIAL_GAMES_RETRIGGER_HIDE, 'complete').promise;
        this.retriggerLeaveCondition.resolve();
    }

    private async onSpecialGamesStartInfoState(): Promise<void> {
        EventsCenter.emit(EGameEvents.SPECIAL_GAMES_START_INFO_SHOW);
        await this.info?.waitForAnimationEvent(EGameEvents.SPECIAL_GAMES_START_INFO_SHOW, 'complete').promise;
        await EventsCenter.waitForEvent(EGameEvents.CONTINUE);
        EventsCenter.emit(EGameEvents.SPECIAL_GAMES_START_INFO_HIDE);
        await this.info?.waitForAnimationEvent(EGameEvents.SPECIAL_GAMES_START_INFO_HIDE, 'complete').promise;
        this.specialGamesStartInfoLeaveCondition.resolve();
    }

    private async onSpecialGamesIntroState(): Promise<void> {
        EventsCenter.emit(EGameEvents.SPECIAL_GAMES_INTRO);
        await this.intro?.waitForAnimationEvent(EGameEvents.SPECIAL_GAMES_INTRO, 'complete').promise;
        this.specialGamesIntroLeaveCondition.resolve();
    }

    private async onSpecialGamesIntroEndState(): Promise<void> {
        EventsCenter.emit(EGameEvents.SPECIAL_GAMES_INTRO_END);
        await this.intro?.waitForAnimationEvent(EGameEvents.SPECIAL_GAMES_INTRO_END, 'complete').promise;
        this.specialGamesIntroEndLeaveCondition.resolve();
    }

    private async onSpecialGamesSpecialSymbolState(): Promise<void> {
        EventsCenter.emit(EGameEvents.SPECIAL_GAMES_SYMBOL_DRAW);
        await this.specialSymbol?.waitForAnimationEvent(EGameEvents.SPECIAL_GAMES_SYMBOL_DRAW, 'complete').promise;
        const specialGamesConfig = ConfigLoader.getConfiguration(EConfigurationNames.GAME).specialGames;
        const symbolDrawAnimationType = specialGamesConfig.symbolDrawAnimationType ?? ESpecialGamesSymbolDrawAnimationType.CLASSIC;
        if (symbolDrawAnimationType === ESpecialGamesSymbolDrawAnimationType.REEL) {
            const { numberOfPlaceholderSlots, attachementPrefix, slotPrefix } = specialGamesConfig.reelAnimationOptions!;
            for (let i = 0; i < numberOfPlaceholderSlots; i++) {
                this.specialSymbol?.spine.skeleton.setAttachment(
                    `${slotPrefix}${i + 1}`,
                    `${attachementPrefix}${this.randomIds[i] < 10 ? '0' : ''}${this.randomIds[i]}`
                );
            }
        }
        EventsCenter.emit(EGameEvents.SPECIAL_GAMES_SYMBOL_SELECT);
        await this.specialSymbol?.waitForAnimationEvent(EGameEvents.SPECIAL_GAMES_SYMBOL_SELECT, 'complete').promise;
        this.specialGamesSpecialSymbolLeaveCondition.resolve();
    }
}
